import React, { useState, useEffect } from 'react'
import {
	Form,
	Button, // Keep Button
	Typography,
	Row,
	Col,
	Space,
	message,
	Modal,
	Input,
	Table,
	Switch,
	Tag,
	Card,
} from 'antd'
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	EyeOutlined,
	GlobalOutlined,
} from '@ant-design/icons'
import { httpGet, httpPost, httpPatch, httpDelete } from '../../common/http.service'

const { Title, Text } = Typography

interface Timezone {
	id: string
	timezone: string
	utcOffset: string
	displayName: string
	isDefault: boolean
	enabled: boolean
	createdAt?: string
	updatedAt?: string
}

const TimezoneManagementTab: React.FC = () => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [timezones, setTimezones] = useState<Timezone[]>([])
	const [defaultTimezone, setDefaultTimezone] = useState<Timezone | null>(null)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [modalMode, setModalMode] = useState<'view' | 'create' | 'edit'>('view')
	const [selectedTimezone, setSelectedTimezone] = useState<Timezone | null>(null)

	useEffect(() => {
		loadTimezones()
		loadDefaultTimezone()
	}, [])

	const loadTimezones = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/webportal/timezone')
			console.log('Timezone response:', response)

			if (response?.data) {
				// Handle both array and single object responses
				const timezoneData = Array.isArray(response.data) ? response.data : [response.data]
				setTimezones(timezoneData)
			} else if (response) {
				// Handle case where data is at root level
				const timezoneData = Array.isArray(response) ? response : [response]
				setTimezones(timezoneData)
			} else {
				setTimezones([])
			}
		} catch (error: any) {
			message.error(error?.message || 'Failed to load timezones')
			setTimezones([])
		} finally {
			setLoading(false)
		}
	}

	const loadDefaultTimezone = async () => {
		try {
			const response: any = await httpGet('/api/v1/webportal/timezone/default')
			console.log('Default timezone response:', response)

			if (response?.data) {
				setDefaultTimezone(response.data)
			} else if (response) {
				// Handle case where data is at root level
				setDefaultTimezone(response)
			} else {
				setDefaultTimezone(null)
			}
		} catch (error: any) {
			console.error('Failed to load default timezone:', error)
			setDefaultTimezone(null)
		}
	}

	const handleView = (timezone: Timezone) => {
		setSelectedTimezone(timezone)
		setModalMode('view')
		form.setFieldsValue(timezone)
		setIsModalVisible(true)
	}

	const handleEdit = (timezone: Timezone) => {
		setSelectedTimezone(timezone)
		setModalMode('edit')
		form.setFieldsValue(timezone)
		setIsModalVisible(true)
	}

	const handleCreate = () => {
		setSelectedTimezone(null)
		setModalMode('create')
		form.resetFields()
		setIsModalVisible(true)
	}

	const handleDelete = (timezone: Timezone) => {
		Modal.confirm({
			title: 'Delete Timezone',
			content: `Are you sure you want to delete this timezone?`,
			okText: 'Delete',
			okType: 'danger',
			onOk: async () => {
				try {
					await httpDelete(`/api/v1/webportal/timezone/${timezone.id}`)
					message.success('Timezone deleted successfully')
					await loadTimezones()
					if (defaultTimezone?.id === timezone.id) {
						await loadDefaultTimezone()
					}
				} catch (error: any) {
					message.error(error?.message || 'Failed to delete timezone')
				}
			},
		})
	}

	const handleModalOk = async () => {
		if (modalMode === 'view') {
			setIsModalVisible(false)
			return
		}

		try {
			const values = await form.validateFields()
			setLoading(true)

			if (modalMode === 'create') {
				await httpPost('/api/v1/webportal/timezone', values)
				message.success('Timezone created successfully')
			} else if (modalMode === 'edit' && selectedTimezone) {
				await httpPatch(`/api/v1/webportal/timezone/${selectedTimezone.id}`, values)
				message.success('Timezone updated successfully')
			}

			setIsModalVisible(false)
			form.resetFields()
			await loadTimezones()
			await loadDefaultTimezone()
		} catch (error: any) {
			if (error?.errorFields) {
				// Form validation error
				return
			}
			message.error(error?.message || 'Failed to save timezone')
		} finally {
			setLoading(false)
		}
	}

	const handleModalCancel = () => {
		setIsModalVisible(false)
		form.resetFields()
	}

	const columns = [
		{
			title: 'Display Name',
			dataIndex: 'displayName',
			key: 'displayName',
		},
		{
			title: 'Timezone',
			dataIndex: 'timezone',
			key: 'timezone',
		},
		{
			title: 'UTC Offset',
			dataIndex: 'utcOffset',
			key: 'utcOffset',
		},
		{
			title: 'Default',
			dataIndex: 'isDefault',
			key: 'isDefault',
			render: (isDefault: boolean) => (
				<Tag color={isDefault ? 'green' : 'default'}>{isDefault ? 'Yes' : 'No'}</Tag>
			),
		},
		{
			title: 'Enabled',
			dataIndex: 'enabled',
			key: 'enabled',
			render: (enabled: boolean) => (
				<Tag color={enabled ? 'blue' : 'red'}>{enabled ? 'Enabled' : 'Disabled'}</Tag>
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: any, record: Timezone) => (
				<Space>
					<Button type='link' icon={<EyeOutlined />} onClick={() => handleView(record)}>
						View
					</Button>
					<Button type='link' icon={<EditOutlined />} onClick={() => handleEdit(record)}>
						Edit
					</Button>
					<Button
						type='link'
						danger
						icon={<DeleteOutlined />}
						onClick={() => handleDelete(record)}
					>
						Delete
					</Button>
				</Space>
			),
		},
	]

	return (
		<Card
			title={
				<span>
					<GlobalOutlined style={{ marginRight: 8 }} />
					Timezone Management
				</span>
			}
			bordered={false}
			style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
			extra={
				<Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
					Create Timezone
				</Button>
			}
		>
			<div style={{ marginBottom: 16 }}>
				<Typography.Paragraph type='secondary'>
					Manage the supported timezones and set a default for the organization.
				</Typography.Paragraph>

				{defaultTimezone && (
					<div
						style={{
							marginBottom: 16,
							padding: '12px 16px',
							background: '#f5f5f5',
							borderRadius: 8,
						}}
					>
						<Text strong>Default Timezone: </Text>
						<Text>{defaultTimezone.displayName}</Text>
						<Text>
							{' '}
							({defaultTimezone.timezone} {defaultTimezone.utcOffset})
						</Text>
					</div>
				)}
			</div>

			<Table
				columns={columns}
				dataSource={timezones}
				loading={loading}
				rowKey='id'
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total) => `Total ${total} timezones`,
				}}
			/>

			<Modal
				title={
					modalMode === 'view'
						? 'View Timezone'
						: modalMode === 'edit'
							? 'Edit Timezone'
							: 'Create Timezone'
				}
				open={isModalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				okText={modalMode === 'view' ? 'Close' : 'Save'}
				cancelButtonProps={{
					style: { display: modalMode === 'view' ? 'none' : 'inline-block' },
				}}
				confirmLoading={loading}
			>
				<Form form={form} layout='vertical'>
					<Form.Item
						label='Display Name'
						name='displayName'
						rules={[{ required: true, message: 'Please input display name' }]}
					>
						<Input
							disabled={modalMode === 'view'}
							placeholder='e.g., Cambodia Time (ICT)'
						/>
					</Form.Item>

					<Form.Item
						label='Timezone'
						name='timezone'
						rules={[{ required: true, message: 'Please input timezone identifier' }]}
					>
						<Input
							disabled={modalMode === 'view'}
							placeholder='e.g., Asia/Phnom_Penh'
						/>
					</Form.Item>

					<Form.Item
						label='UTC Offset'
						name='utcOffset'
						rules={[
							{ required: true, message: 'Please input UTC offset' },
							{
								pattern: /^[+-]\d{2}:\d{2}$/,
								message: 'Format must be +HH:MM or -HH:MM',
							},
						]}
					>
						<Input disabled={modalMode === 'view'} placeholder='e.g., +07:00' />
					</Form.Item>

					<Form.Item
						label='Set as Default'
						name='isDefault'
						valuePropName='checked'
						initialValue={false}
					>
						<Switch disabled={modalMode === 'view'} />
					</Form.Item>

					<Form.Item
						label='Enabled'
						name='enabled'
						valuePropName='checked'
						initialValue={true}
					>
						<Switch disabled={modalMode === 'view'} />
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	)
}

export default TimezoneManagementTab
